import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FlatList } from '@codehesion-za/headless';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import { Button, LoadingContainer } from '@project/components';
import { useGetMagazinesQuery, MagazineListItemType } from '@project/queries';
import { MagazineCard } from './magazine.card';

const PER_PAGE = 12;

const ListEmptyComponent = <span className="text-gray-600">No magazines to show</span>;

export const MagazinesPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { data, isLoading } = useGetMagazinesQuery(page, PER_PAGE);

  const goToSubscribe = () => navigate('/partner/magazines/subscribe');

  const meta = data?.meta;

  const RenderItem = ({ item, index }: { item: MagazineListItemType; index: number }) => (
    <div key={`${item.id}-${index}`} className="p-2">
      <MagazineCard magazine={item} onSubscribe={goToSubscribe} />
    </div>
  );

  return (
    <div className="p-6 md:p-14">
      <p className="text-lg">Magazines</p>
      <div className="w-16 rounded border-b-2 border-gold" />
      <p className="mt-2 text-sm text-gray-400">
        Browse our latest magazines. Paid issues require an active subscription.
      </p>

      <div className="mt-8">
        <LoadingContainer isLoading={isLoading}>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <FlatList
              items={data?.magazines ?? []}
              renderItem={RenderItem}
              ListEmptyComponent={ListEmptyComponent}
            />
          </div>
        </LoadingContainer>
      </div>

      {meta && meta.totalPages > 1 && (
        <div className="mt-8 flex items-center justify-center gap-4">
          <Button
            variant="text"
            isDisabled={!meta.prevPage}
            onClick={() => meta.prevPage && setPage(meta.prevPage)}
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </Button>
          <span className="text-sm text-gray-600">
            Page {meta.currentPage} of {meta.totalPages}
          </span>
          <Button
            variant="text"
            isDisabled={!meta.nextPage}
            onClick={() => meta.nextPage && setPage(meta.nextPage)}
          >
            <ChevronRightIcon className="h-5 w-5" />
          </Button>
        </div>
      )}
    </div>
  );
};
